import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { AlarmClock, FileClock, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, IconChip } from '@/components/ui';
import { CONTRACTS_KEY, contractsApi } from '@/features/contracts/contracts-api';
import { intlLocale } from '@/lib/i18n';
import { cn } from '@/lib/utils';

const dateFmt = new Intl.DateTimeFormat(intlLocale(), {
  timeZone: 'UTC',
  day: 'numeric',
  month: 'numeric',
});
const fmt = (d: string) => dateFmt.format(new Date(`${d}T12:00:00Z`));
const today = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Jerusalem' }).format(new Date());
const daysUntil = (d: string) =>
  Math.round((Date.parse(`${d}T12:00:00Z`) - Date.parse(`${today()}T12:00:00Z`)) / 86_400_000);

/** בסקירה: חוזים שצריך להחליט עליהם ואחריות שנגמרת — לפני שזה מאוחר. */
export function ContractsWatch() {
  const { t } = useTranslation('dashboard');
  const list = useQuery({
    queryKey: CONTRACTS_KEY,
    queryFn: ({ signal }) => contractsApi.list(signal),
  });
  const items = (list.data ?? [])
    .flatMap((c) => {
      const out: Array<{
        id: string;
        title: string;
        when: string;
        days: number;
        kind: 'notice' | 'warranty';
      }> = [];
      if ((c.state === 'decide_soon' || c.state === 'notice_passed') && c.noticeDate) {
        out.push({
          id: `${c.id}:n`,
          title: c.title,
          when: c.noticeDate,
          days: daysUntil(c.noticeDate),
          kind: 'notice',
        });
      }
      if (c.warrantyUntil && daysUntil(c.warrantyUntil) >= 0 && daysUntil(c.warrantyUntil) <= 30) {
        out.push({
          id: `${c.id}:w`,
          title: c.title,
          when: c.warrantyUntil,
          days: daysUntil(c.warrantyUntil),
          kind: 'warranty',
        });
      }
      return out;
    })
    .sort((a, b) => a.days - b.days)
    .slice(0, 5);

  return (
    <Card className={cn(items.length > 0 && 'border-warning-border')}>
      <CardHeader tone="info" className="flex items-center justify-between">
        <CardTitle className="flex items-center gap-2.5">
          <IconChip icon={FileClock} tone="info" variant="solid" size="sm" />
          {t('contracts.title')}
        </CardTitle>
        <Link to="/contracts" className="text-xs font-medium text-accent hover:underline">
          {t('contracts.viewAll')}
        </Link>
      </CardHeader>
      <CardContent className="pt-3">
        {list.isPending ? null : items.length === 0 ? (
          <p className="text-sm text-fg-muted">
            {list.data?.length ? t('contracts.noneSoon') : t('contracts.noneYet')}
          </p>
        ) : (
          <ul className="space-y-1">
            {items.map((i) => (
              <li key={i.id}>
                <Link
                  to="/contracts"
                  className="-mx-2 flex items-center gap-2 rounded-lg px-2 py-2 hover:bg-surface-hover"
                >
                  {i.kind === 'notice' ? (
                    <AlarmClock className="size-4 shrink-0 text-warning" aria-hidden />
                  ) : (
                    <ShieldCheck className="size-4 shrink-0 text-warning" aria-hidden />
                  )}
                  <span className="min-w-0 flex-1 truncate text-sm text-fg">{i.title}</span>
                  <span className="shrink-0 text-xs text-fg-muted">
                    {t(i.kind === 'notice' ? 'contracts.decideBy' : 'contracts.warrantyUntil', {
                      date: fmt(i.when),
                    })}
                    {' · '}
                    {i.days >= 0
                      ? t('contracts.daysLeft', { count: i.days })
                      : t('contracts.passed')}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
